import { useEffect, useState } from "react";
import { isVitalsSubdomain } from "../util/subdomain";

export const REDIRECT_COUNTDOWN_SECONDS = 4;

/**
 * Hook to detect if the app is running on vitals.circadia.link subdomain
 * or localhost/172.20.10.2 (for testing) and redirect to the vitals-ai route immediately on page load.
 * Exceptions:
 * - Allows access to login pages - users must login first
 * - Allows access to change-facility page - users can switch facilities
 * - Allows access to patient profile pages for vitals history viewing
 * - For status=3 (resident did not consent): shows consent message for 4 seconds then redirects to vitals-ai
 * - Allows access to patient profiles with other consent status for back navigation
 */
export const useVitalsSubdomainRedirect = () => {
  const [showCountdown, setShowCountdown] = useState(false);

  const handleRedirect = () => {
    const redirectUrl = `${window.location.origin}/#/vitals-ai`;
    window.location.href = redirectUrl;
  };

  const checkAndRedirect = () => {
    if (!isVitalsSubdomain()) return;

    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;

    const isAlreadyOnVitalsAI =
      currentHash === "#/vitals-ai" || currentPath.includes("vitals-ai");
    const isOnPatientProfile =
      currentHash.includes("#/patient-profile") ||
      currentPath.includes("patient-profile");
    const isOnLoginPage =
      currentHash.includes("#/login") || currentPath.includes("login");
    const isSSOLoginPage =
      currentHash.includes("#/sso/login") || currentPath.includes("sso/login");
    const isOnUserInfoPage =
      currentHash.includes("#/user-info") || currentPath.includes("user-info");
    const isOnChangeFacility =
      currentHash.includes("#/change-facility") ||
      currentPath.includes("change-facility");
    const hasConsentStatus =
      currentSearch.includes("status=") ||
      currentSearch.includes("consent") ||
      currentSearch.includes("denied") ||
      currentSearch.includes("archived");
    const hasStatus3 =
      currentSearch.includes("status=3") || currentHash.includes("status=3");

    if (isOnLoginPage || isSSOLoginPage || isOnUserInfoPage) {
      setShowCountdown(false);
      return;
    }

    if (isOnChangeFacility) {
      setShowCountdown(false);
      return;
    }

    if (hasStatus3 && isOnPatientProfile) {
      setShowCountdown(true);
      return;
    } else {
      setShowCountdown(false);
    }

    if (
      !isAlreadyOnVitalsAI &&
      !isOnPatientProfile &&
      !isOnLoginPage &&
      !isSSOLoginPage &&
      !isOnChangeFacility &&
      !hasConsentStatus &&
      !isOnUserInfoPage
    ) {
      const redirectUrl = `${window.location.origin}/#/vitals-ai${currentSearch}`;

      try {
        window.location.href = redirectUrl;
      } catch (error) {
        console.error("Failed to redirect to vitals-ai:", error);
        try {
          window.history.replaceState(null, "", redirectUrl);
          window.location.reload();
        } catch (fallbackError) {
          console.error("Fallback redirect also failed:", fallbackError);
        }
      }
    }
  };

  useEffect(() => {
    checkAndRedirect();

    const handleHashChange = () => checkAndRedirect();
    const handlePopState = () => checkAndRedirect();

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return {
    isVitalsSubdomain: isVitalsSubdomain(),
    showCountdown,
    handleRedirect,
  };
};
